import React, { CSSProperties } from 'react';
import { IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { merge } from 'lodash';
import { t, resolvePreviewColor } from '@core/utils';
import { BasicBlock } from '@core/components/BasicBlock';

export type ISection = IBlockData<
	{
		'background-color'?: string;
		'background-position'?: string;
		'background-position-x'?: string;
		'background-position-y'?: string;
		'background-repeat'?: 'repeat' | 'no-repeat';
		'background-size'?: string;
		'background-url'?: string;
		border?: string;
		'border-radius'?: string;
		direction?: 'ltr' | 'rtl';
		'full-width'?: string;
		padding?: string;
		'text-align'?: CSSProperties['textAlign'];
		'max-width'?: string;
		'border-width'?: string;
		'border-style'?: string;
		'border-color'?: string;
		'border-top-width'?: string;
		'border-right-width'?: string;
		'border-bottom-width'?: string;
		'border-left-width'?: string;
	},
	{
		noWrap?: boolean;
		selectedListingId?: string;
		selectedListing?: Record<string, unknown> | null;
	}
>;

export const Section = createBlock<ISection>({
	get name() {
		return t('Section');
	},
	type: BasicType.SECTION,
	create: payload => {
		const defaultData: ISection = {
			type: BasicType.SECTION,
			data: {
				value: {
					noWrap: false,
				},
			},
			attributes: {
				padding: '0px 0px 0px 0px',
				'background-repeat': 'repeat',
				'background-size': 'auto',
				'background-position': 'top center',
				border: 'none',
				direction: 'ltr',
				'text-align': 'center',
				'background-color': '#FFFFFF',
				'border-width': '0px',
				'border-style': 'solid',
				'border-color': '#000000',
				'border-top-width': '',
				'border-right-width': '',
				'border-bottom-width': '',
				'border-left-width': '',
			},
			children: [],
		};
		return merge(defaultData, payload);
	},
	// 'custom_listing_block' is included so section layouts can be DROPPED onto
	// the Custom Listing container; useBlock then adopts only the columns (a
	// section is never actually nested inside it — see addBlock/moveBlock).
	validParentType: [BasicType.PAGE, BasicType.WRAPPER, 'custom_listing_block'],
	render(params) {
		const { data } = params;

		const borderWidth = data.attributes['border-width'];
		const borderStyle = data.attributes['border-style'];
		const borderColor = resolvePreviewColor(
			data.attributes['border-color'],
			params.mode,
			params.dataSource,
		);
		const borderTopWidth = data.attributes['border-top-width'];
		const borderRightWidth = data.attributes['border-right-width'];
		const borderBottomWidth = data.attributes['border-bottom-width'];
		const borderLeftWidth = data.attributes['border-left-width'];

		let attributes = data.attributes;

		const hasSideBorderWidths = [
			borderTopWidth,
			borderRightWidth,
			borderBottomWidth,
			borderLeftWidth,
		].some(value => value !== undefined && value !== null && value !== '');

		if (hasSideBorderWidths) {
			const normalizedBorderStyle = borderStyle || 'solid';
			const normalizedBorderColor = borderColor || '#000000';

			attributes = {
				...data.attributes,
				border: 'none',
				'border-top': `${borderTopWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
				'border-right': `${borderRightWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
				'border-bottom': `${borderBottomWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
				'border-left': `${borderLeftWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
			};
		}

		if (!hasSideBorderWidths && (borderWidth || borderStyle || borderColor)) {
			const combinedBorder = [
				borderWidth || '0px',
				borderStyle || 'solid',
				borderColor || '#000000',
			].join(' ');

			attributes = {
				...data.attributes,
				border: combinedBorder,
			};
		}

		if (params.mode !== 'production') {
			attributes = {
				...attributes,
				'full-width': 'full-width',
			};
		}
		return (
			<BasicBlock
				params={{
					...params,
					data: {
						...data,
						attributes,
					},
				}}
				tag='mj-section'
			/>
		);
	},
});
